import { LegalLayout } from "./LegalLayout";

const CONTACT_EMAIL = "bmabma79@gmail.com";

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="22 June 2026">
      <p>
        This Privacy Policy explains how <strong>African Polistas</strong>{" "}
        (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects, uses, and protects information in
        connection with our internal Operations Dashboard, including the
        associated WhatsApp Business and Telegram bots
        (collectively, the &ldquo;Service&rdquo;) available at{" "}
        <a href="https://dashboard.africanpolistas.com">
          dashboard.africanpolistas.com
        </a>
        .
      </p>

      <p>
        The Service is a private operational tool used by authorised African
        Polistas staff and executives to manage equestrian operations. It is
        not a consumer product and is not offered to the general public.
      </p>

      <h2>1. Who this policy applies to</h2>
      <ul>
        <li>
          <strong>Staff members</strong> who interact with the Service by
          sending messages, photos, or documents through WhatsApp or Telegram.
        </li>
        <li>
          <strong>Executives and administrators</strong> who sign in to the
          web dashboard.
        </li>
      </ul>

      <h2>2. Information we collect</h2>
      <h3>2.1 Account information</h3>
      <ul>
        <li>Username, display name, and role (set by an administrator).</li>
        <li>
          Authentication identifiers provided by our identity provider, Clerk
          (
          <a href="https://clerk.com/privacy">clerk.com/privacy</a>).
        </li>
      </ul>

      <h3>2.2 Messaging information</h3>
      <ul>
        <li>
          Phone number (WhatsApp ID) and any message content sent to our
          official WhatsApp business number or Telegram bot, including text,
          photos, and PDF documents (typically invoices).
        </li>
        <li>
          Timestamps and delivery/read statuses returned by WhatsApp and
          Telegram.
        </li>
      </ul>

      <h3>2.3 Operational data</h3>
      <ul>
        <li>
          Records that staff submit through the bots or the dashboard, such as
          horse profiles, feed logs, inventory levels, vehicle maintenance
          notes, and invoice extractions.
        </li>
        <li>
          An audit trail of changes made by signed-in users (who changed
          what, and when).
        </li>
      </ul>

      <h3>2.4 Technical data</h3>
      <ul>
        <li>
          IP addresses, browser/device information, and error diagnostics
          captured by our error-monitoring provider, Sentry
          (<a href="https://sentry.io/privacy/">sentry.io/privacy</a>),
          including limited session replay information when an error occurs.
        </li>
      </ul>

      <h2>3. How we use your information</h2>
      <ul>
        <li>To authenticate users and authorise access to dashboard data.</li>
        <li>
          To process operational requests sent through the bots (e.g. log
          feed quantities, extract invoice details, update horse records).
        </li>
        <li>
          To maintain accurate operational records and audit history for the
          business.
        </li>
        <li>
          To diagnose and fix bugs, prevent abuse, and improve the
          reliability of the Service.
        </li>
      </ul>

      <h2>4. Automated processing (AI)</h2>
      <p>
        We use third-party Large Language Model (LLM) services to:
      </p>
      <ul>
        <li>
          Extract structured data from invoice images and PDFs submitted by
          staff.
        </li>
        <li>
          Translate plain-language &ldquo;quick edit&rdquo; instructions issued by
          administrators into structured updates against our database.
        </li>
      </ul>
      <p>
        Content submitted to these providers may be processed on their
        infrastructure subject to their respective data-handling policies.
        We do not knowingly send personal identifiers beyond what is required
        to perform the requested task.
      </p>

      <h2>5. How we share information</h2>
      <p>
        We do not sell personal information. We share information only with:
      </p>
      <ul>
        <li>
          <strong>Service providers</strong> who help us operate the Service,
          including: Clerk (authentication), Meta / WhatsApp Cloud API
          (messaging), Telegram (messaging), MongoDB (database hosting),
          Sentry (error monitoring), and the LLM providers used for invoice
          extraction and quick edit.
        </li>
        <li>
          <strong>Authorised African Polistas personnel</strong> for
          legitimate operational purposes.
        </li>
        <li>
          <strong>Authorities</strong>, when required by applicable law or
          legal process.
        </li>
      </ul>

      <h2>6. Data retention</h2>
      <p>
        We retain operational records for as long as they are useful for the
        running of the business and as required by applicable law. Audit-log
        entries are kept indefinitely to preserve historical accountability.
        Personally identifying details for an individual staff member can be
        removed on request (see Section 9).
      </p>

      <h2>7. Security</h2>
      <ul>
        <li>
          All web traffic is encrypted in transit using TLS/HTTPS.
        </li>
        <li>
          Webhook traffic from Meta is verified using HMAC signatures, and
          duplicate messages are deduplicated at our backend.
        </li>
        <li>
          Access to the dashboard is restricted to members of the African
          Polistas organisation in Clerk.
        </li>
        <li>
          Group chats with the WhatsApp bot are intentionally ignored to
          prevent accidental disclosure of operational data.
        </li>
      </ul>

      <h2>8. International transfers</h2>
      <p>
        Our service providers may process data in jurisdictions other than
        the one in which you reside, including the United States, the
        European Union, and South Africa. By using the Service you
        acknowledge that your information may be transferred to, and
        processed in, such jurisdictions.
      </p>

      <h2>9. Your rights</h2>
      <p>
        Subject to applicable law, you may request to:
      </p>
      <ul>
        <li>Access the personal information we hold about you.</li>
        <li>Correct inaccurate information.</li>
        <li>Have personally identifying information deleted.</li>
        <li>Withdraw consent for use of the Service.</li>
      </ul>
      <p>
        To exercise any of these rights, contact us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We may need
        to verify your identity before acting on a request. See also our{" "}
        <a href="/data-deletion">Data Deletion Instructions</a>.
      </p>

      <h2>10. Children</h2>
      <p>
        The Service is intended for staff and management use only. It is not
        directed to anyone under the age of 16, and we do not knowingly
        collect personal information from children.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes
        will be communicated via the dashboard or to administrators via the
        bot.
      </p>

      <h2>12. Contact</h2>
      <p>
        For any privacy-related question or request, write to:{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}
